// ─── Recibir dinero Colombia · Side panel (Bre-B keys + QR · PSE · Nequi push · Botón Bancolombia) ─
// Opened from the COP wallet card. The first screen lists every Bre-B key (copy, its own QR), then a 5-step charge:
//   0. Amount + method — ONE amount and, when it carries a commission, the send panel's "who covers it" switch
//      (on: it comes out of what is credited; off: the payer sends it on top); the live fee preview shows the
//      breakdown before creating. Then the rail: Bre-B needs nothing else, the payer rails need the payer.
//   1. Payer — PSE renders the shared `PsePayerStep` (identical to `/app/payins`); Nequi and Botón
//      Bancolombia render `ColombiaPayerStep` (same field contract, no bank picker or person-type mandate).
//   2. Confirmation — full money trail (pays / fee / credited) + the method's own rows
//   3. Processing (API call, modal locked)
//   4. Result — Bre-B: QR rendered client-side from the EMV payload. PSE / Botón Bancolombia:
//      automatic same-window handoff to the bank. Nequi: "check your Nequi app" wait state (the charge was
//      pushed to the payer's phone). All live: `usePayinLiveStatus` (socket `payin:updated` + polling
//      fallback) flips the step the moment the provider webhook settles or rejects the payin.
//
// Uses the NEW NestJS payments runtime (`POST /internal/payments/payins`, rails BREB | PSE | NEQUI |
// BANCOLOMBIA_BUTTON), not the legacy PHP proxy — fees are computed server-side. No return_url is sent for
// the redirect rails: the backend env default (this dashboard's result page) is used, and both flows arrive
// there with ?payin=<id> — PSE via our callback, Botón Bancolombia baked into the registered URL.

import { AlertCircle, ArrowRight, CheckCircle2, ChevronLeft, KeyRound, Loader2, XCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { newIdempotencyKey } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { PayinQrCard } from "@/modules/dashboard/payins/components/PayinQrCard";
import {
  isTerminalPayinStatus,
  usePayinLiveStatus,
} from "@/modules/dashboard/payins/hooks/usePayinLiveStatus";
import {
  buildColombiaBancolombiaButtonPayinPayload,
  buildColombiaBrebPayinPayload,
  buildColombiaNequiPayinPayload,
  buildColombiaPsePayinPayload,
  type InitiatePayinPayload,
  type InitiatePayinResponse,
  PayinRail,
  PayinRuntimeStatus,
  PaymentsPayinService,
  type PreviewPayinResponse,
  readPayinErrorCode,
} from "@/modules/dashboard/payins/services/paymentsPayin.service";
import { pixQrDataUrl } from "@/modules/dashboard/payins/utils/pixQrDataUrl";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaStepper, type ColocaStepperStep } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import { BrebKeyEntryCard } from "@/shared/components/payments/breb-key/BrebKeyEntryCard";
import { BrebKeyFlow, BrebKeyWizardStepper } from "@/shared/components/payments/breb-key/BrebKeyFlow";
import { BrebKeyStaticQr } from "@/shared/components/payments/breb-key/BrebKeyStaticQr";
import { BrebReceivingKeySelect } from "@/shared/components/payments/breb-key/BrebReceivingKeySelect";
import { type ColombiaPayerValues, ColombiaPayerStep } from "@/shared/components/payments/ColombiaPayerStep";
import { ColombiaPayinMethodChoice } from "@/shared/components/payments/ColombiaPayinMethodChoice";
import {
  ALL_COLOMBIA_PAYIN_METHODS,
  COLOMBIA_REDIRECT_METHODS,
  type ColombiaPayinMethod,
  colombiaMethodNeedsPayer,
} from "@/shared/components/payments/colombiaPayinMethods";
import { NequiWaitingScreen } from "@/shared/components/payments/NequiWaitingScreen";
import {
  PayinAmountWithFee,
  type PayinAmountWithFeeState,
} from "@/shared/components/payments/PayinAmountWithFee";
import { type PayinConfirmRow, PayinConfirmSummary } from "@/shared/components/payments/PayinConfirmSummary";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import { PseFailureScreen, PseHandoffScreen } from "@/shared/components/payments/pse/PseHandoffScreens";
import { psePayerUserType, type PsePayerValues } from "@/shared/components/payments/pse/psePayer.schema";
import { PsePayerStep } from "@/shared/components/payments/pse/PsePayerStep";
import { usePseRedirect } from "@/shared/components/payments/pse/usePseRedirect";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { refreshBrebReceivingKeys, useBrebReceivingKey } from "@/shared/hooks/useBrebReceivingKey";
import { useTaxLabel } from "@/shared/hooks/useTaxLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { HttpApiService } from "@/shared/services/httpApi.service";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import {
  type AmountBounds,
  type AmountLimitViolation,
  boundsForPayer,
  formatLimitAmount,
  payerTypeForColombianPayer,
  resolveAmountLimitViolation,
} from "@/shared/utils/amountLimits";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { splitFeeTotal } from "@/shared/utils/paymentTaxes";
import { formatRail } from "@/shared/utils/railDisplay";

import { CURRENCY_TO_COUNTRY } from "../constants/wallets.constants";
import { useDepositAmountLabels, useDepositFeeSwitchLabels } from "../hooks/useDepositAmountLabels";
import type { Wallet } from "../types/wallets-api.types";
import { DepositOutcomeBanner } from "./deposit/DepositOutcomeBanner";

// ─── Types ───────────────────────────────────────────────────────────────────

const STEP = { AMOUNT: 0, PAYER: 1, CONFIRM: 2, PROCESSING: 3, RESULT: 4 } as const;
type ModalStep = (typeof STEP)[keyof typeof STEP];

/** The payer form lives in the body, its submit button in the dialog footer — wired by this id. */
const PAYER_FORM_ID = "colombia-deposit-payer";

// The rails that hand the payer off to a bank page now live beside the method list, shared with
// `/app/payins` — the two surfaces must not disagree about what a rail means.

interface WizardState {
  method: ColombiaPayinMethod;
  /** Bre-B only: another ACTIVE organization key to mint the QR over; null keeps the primary one. */
  brebKeyId: string | null;
  /** Payer data. Ignored for Bre-B — a dynamic QR needs no payer identity. */
  payer: PsePayerValues | null;
  /** Display name of the selected bank, captured at selection for the confirm row and the return page. */
  bankName: string;
}

/** `key`: the organization's own Bre-B keys, created or managed here. `qr`: one key's any-amount QR. */
export type ColombiaDepositView = "deposit" | "key" | "qr";

export interface ColombiaDepositModalProps {
  wallet: Wallet;
  open: boolean;
  onClose: () => void;
  initialView?: ColombiaDepositView;
  /** The key the `qr` and `key` views open on; the primary one otherwise. */
  initialKeyId?: string | null;
}

// ─── Module-level constants ───────────────────────────────────────────────────

const EMPTY_PRICED: PayinAmountWithFeeState = {
  mode: "gross",
  preview: null,
  amount: null,
  loading: false,
  error: null,
};

const INITIAL_WIZARD: WizardState = {
  method: PayinRail.BREB,
  brebKeyId: null,
  payer: null,
  bankName: "",
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Money arrives as a decimal string; COP settles with no cents, which `formatCurrencyAmount` already knows. */
const formatCop = (value: string | null | undefined, locale: string): string =>
  formatCurrencyAmount(value, "COP", locale);

// ─── Step 0 · Amount + method ─────────────────────────────────────────────────

interface StepAmountProps {
  method: ColombiaPayinMethod;
  organizationId: string | null;
  priced: PayinAmountWithFeeState;
  /** Known once the payer step was filled and the user walked back; null on the first pass. */
  payerType: LimitPayerType | null;
  onPricedChange: (state: PayinAmountWithFeeState) => void;
  onMethodChange: (method: ColombiaPayinMethod) => void;
  brebKeyId: string | null;
  onBrebKeyChange: (keyId: string | null) => void;
  onOpenKey: () => void;
  onShowKeyQr: (keyId: string) => void;
}

/**
 * ONE amount, and a switch deciding who covers the commission — what the payer sends, or what must land in
 * the wallet — priced live by the fee preview for the SELECTED method: the rail is part of the corridor, so
 * switching methods re-prices the same amount (their commissions differ). The control itself is shared with
 * Brazil and the `/app/payins` forms; only the copy and the corridor differ, and a corridor with no pricing
 * surfaces the error inside it rather than after the payer form — creating the payin would fail on the same
 * missing configuration anyway.
 *
 * `initialAmount` is what makes "back" honest: this component unmounts when the wizard advances, so without
 * the seed the field would come up empty while a stale preview still gated the footer.
 */
function StepAmount({
  method,
  organizationId,
  priced,
  payerType,
  onPricedChange,
  onMethodChange,
  brebKeyId,
  onBrebKeyChange,
  onOpenKey,
  onShowKeyQr,
}: StepAmountProps) {
  const labels = useDepositAmountLabels();
  const feeSwitch = useDepositFeeSwitchLabels();
  const { t } = useTranslations("wallets");

  return (
    <div className="space-y-6">
      {/* First, because it is the path that works from a single phone: no amount, no QR to scan. */}
      <BrebKeyEntryCard layout="list" onOpen={onOpenKey} onShowQr={onShowKeyQr} />
      <div className="space-y-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">{t("deposit_amount_section_title")}</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{t("deposit_amount_section_hint")}</p>
        </div>
        <PayinAmountWithFee
          organizationId={organizationId}
          rail={method}
          country="COL"
          currency="COP"
          labels={labels}
          feeSwitch={feeSwitch}
          quickAmounts={quickAmountsFor("COP")}
          variant="prominent"
          initialAmount={priced.amount}
          initialMode={priced.mode}
          payerType={payerType}
          onChange={onPricedChange}
        />
        {/* All four: this modal builds a payload for each one and shows each one's own outcome screen —
            see the builder switch in `handleConfirm`. */}
        <ColombiaPayinMethodChoice
          value={method}
          onChange={onMethodChange}
          methods={ALL_COLOMBIA_PAYIN_METHODS}
        />
        {method === PayinRail.BREB && <BrebReceivingKeySelect value={brebKeyId} onChange={onBrebKeyChange} />}
      </div>
    </div>
  );
}

// ─── Step 2 · Confirmation ────────────────────────────────────────────────────

interface StepConfirmProps {
  wizard: WizardState;
  preview: PreviewPayinResponse;
  /** The exact cap of the payer kind that is now known, when the confirmed gross falls outside it. */
  limitViolation: AmountLimitViolation | null;
  limitBounds: AmountBounds | null;
  payerType: LimitPayerType | null;
  onEditAmount: () => void;
}

function StepConfirm({
  wizard,
  preview,
  limitViolation,
  limitBounds,
  payerType,
  onEditAmount,
}: StepConfirmProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const taxLabel = useTaxLabel();
  const { t: tPayins } = useTranslations("payins");
  const isPse = wizard.method === PayinRail.PSE;
  const payer = wizard.payer;

  const fee = splitFeeTotal(preview.fee_total, preview.taxes);
  const rows: PayinConfirmRow[] = [
    { label: t("deposit_payer_sends"), value: `${formatCop(preview.gross_amount, activeLanguage)} COP` },
    { label: t("deposit_breakdown_fee"), value: `${formatCop(fee.commission, activeLanguage)} COP` },
    ...fee.taxes.map((tax) => ({
      label: taxLabel(tax),
      value: `${formatCop(tax.amount, activeLanguage)} COP`,
    })),
    {
      label: t("deposit_breakdown_credited"),
      value: `${formatCop(preview.net_amount, activeLanguage)} COP`,
    },
    {
      label: t("deposit_confirm_method_row"),
      value: isPse ? t("deposit_method_pse") : formatRail(wizard.method),
    },
  ];

  if (payer) {
    if (isPse) {
      rows.push({ label: t("deposit_pse_bank_label"), value: wizard.bankName || payer.bankCode });
    }
    rows.push(
      { label: t("deposit_payer_name_label"), value: payer.payerName.trim() },
      {
        label: t("deposit_pse_doc_number_label"),
        value: `${payer.documentType} ${payer.documentNumber}`,
      },
    );
    if (wizard.method === PayinRail.NEQUI) {
      rows.push({ label: t("deposit_form_nequi"), value: payer.phone });
    }
  }

  return (
    <div className="space-y-4">
      {limitViolation && limitBounds && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={tPayins(limitViolation === "above_maximum" ? "confirm_limit_above" : "confirm_limit_below", {
            payer: tPayins(payerType === "COMPANY" ? "limit_payer_company" : "limit_payer_person"),
            max: formatLimitAmount(limitBounds.max_amount ?? "0", "COP", activeLanguage),
            min: formatLimitAmount(limitBounds.min_amount ?? "0", "COP", activeLanguage),
          })}
          actions={
            <ColocaButton size="sm" variant="default" onClick={onEditAmount}>
              {tPayins("confirm_limit_edit_amount")}
            </ColocaButton>
          }
        />
      )}
      <PayinConfirmSummary title={t("deposit_confirm_amount")} rows={rows} />
    </div>
  );
}

// ─── Step 3 · Processing ──────────────────────────────────────────────────────

interface StepProcessingProps {
  payload: InitiatePayinPayload;
  onSuccess: (data: InitiatePayinResponse) => void;
  /** `code` is the network's rejection detail on a deterministic 400 (e.g. FAIL_EXCEEDEDLIMIT). */
  onError: (message: string, code: string | null) => void;
}

function StepProcessing({ payload, onSuccess, onError }: StepProcessingProps) {
  const { t } = useTranslations("wallets");

  useEffect(() => {
    let cancelled = false;

    // `initiatePayinOnce` is what makes this effect safe under StrictMode's double mount — see the service.
    PaymentsPayinService.initiatePayinOnce(payload)
      .then((res) => {
        if (!cancelled) onSuccess(res.data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (payload.breb_receiving_key_id) refreshBrebReceivingKeys();
        onError(HttpApiService.getErrorMessage(err), readPayinErrorCode(err));
      });

    return () => {
      cancelled = true;
    };
    // Fires once per mount — payload is stable; the service dedupes the POST.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <Loader2 className="size-10 animate-spin text-primary motion-reduce:animate-none" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{t("deposit_processing")}</p>
    </div>
  );
}

// ─── Step 4 · Result ──────────────────────────────────────────────────────────

interface StepResultProps {
  result: InitiatePayinResponse | null;
  error: string | null;
  /** Network rejection detail from a deterministic initiate 400 (e.g. FAIL_EXCEEDEDLIMIT). */
  errorCode: string | null;
  wizard: WizardState;
  pseReference: string;
  onReset: () => void;
  onClose: () => void;
}

function StepResult({ result, error, errorCode, wizard, pseReference, onReset, onClose }: StepResultProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const taxLabel = useTaxLabel();
  const [qrSrc, setQrSrc] = useState<string | null>(null);

  const isRedirect = !!result?.rail && COLOMBIA_REDIRECT_METHODS.has(result.rail);
  const isNequi = result?.rail === PayinRail.NEQUI;
  // Live for every rail: the payer authorizes at their bank (redirect rails), accepts the push (Nequi) or
  // scans the QR (Bre-B) while this modal stays open, so the outcome is worth surfacing the moment the
  // webhook lands. The hook combines the `payin:updated` socket event with a polling fallback.
  const liveStatus = usePayinLiveStatus(result?.id ?? null, { expiresAt: result?.expires_at });
  const isPaid = liveStatus === PayinRuntimeStatus.CONFIRMED;
  const isTerminal = isTerminalPayinStatus(liveStatus);

  // The redirect rails mandate an AUTOMATIC same-window handoff to the bank — never a manual button. Shared
  // with `/app/payins` so every surface hands off, and persists the return context, identically. Inert for
  // the other rails (the hook's own guards).
  const { failureDetail } = usePseRedirect({
    payin: result,
    bankName: wizard.bankName,
    reference: pseReference,
    isTerminal,
  });

  // The API returns the EMV Bre-B string; the card shows its skeleton until the QR is drawn.
  useEffect(() => {
    if (!result?.qr_code_payload) return;
    let cancelled = false;
    void pixQrDataUrl(result.qr_code_payload).then((url) => {
      if (!cancelled) setQrSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [result?.qr_code_payload]);

  // Error branch — the redirect rails get the mandated early-error screen (copy keyed by the network
  // detail; the merchant contact lines are rail-agnostic).
  if (error) {
    if (COLOMBIA_REDIRECT_METHODS.has(wizard.method)) {
      return <PseFailureScreen detail={errorCode} message={error} onRetry={onReset} />;
    }
    return (
      <div className="flex flex-col items-center gap-4 py-8 text-center">
        <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
          <XCircle className="size-7 text-destructive" aria-hidden="true" />
        </div>
        <div>
          <p className="font-semibold text-destructive">{t("deposit_result_error")}</p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">{error}</p>
        </div>
        <div className="flex gap-2 pt-2">
          <ColocaButton variant="ghost" onClick={onClose}>
            {t("deposit_close")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={onReset}>
            {t("deposit_retry")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  if (!result) return null;

  // Redirect rail rejected before the payer ever reached the bank (registration rejection) — early-error.
  if (isRedirect && isTerminal && !isPaid) {
    return <PseFailureScreen detail={failureDetail} onRetry={onReset} />;
  }

  // Redirect rails waiting for the bank page. The hook above takes the payer to the bank in THIS window the
  // moment the URL exists — no manual button, per the network's mandate.
  if (isRedirect && !isPaid) {
    return <PseHandoffScreen onCancel={onReset} />;
  }

  // Nequi: the charge is already in the payer's app — a wait state, not an instrument. The live-status hook
  // flips this screen to paid/rejected the moment the webhook lands.
  if (isNequi && !isTerminal) {
    return (
      <NequiWaitingScreen
        title={t("deposit_nequi_waiting_title")}
        body={t("deposit_nequi_waiting_body", { phone: wizard.payer?.phone ?? "" })}
        liveLabel={t("deposit_nequi_waiting_live")}
        amount={`${formatCop(result.gross_amount, activeLanguage)} COP`}
        actionLabel={t("deposit_new")}
        onNew={onReset}
      />
    );
  }

  const totalFee = Number(result.fee_total);
  const resultFee = splitFeeTotal(result.fee_total, result.taxes);
  const showQr = !!result.qr_code_payload && !isTerminal;
  const expiresLabel = result.expires_at
    ? new Date(result.expires_at).toLocaleString(activeLanguage, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div className="space-y-4">
      {/* Live outcome: the webhook settled (or rejected) the payin while the modal was open. */}
      <DepositOutcomeBanner status={liveStatus} />

      {/* Side by side on a wide screen so the whole step fits without scrolling; stacked on a phone. */}
      <div
        className={cn(
          showQr &&
            "space-y-4 sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start sm:gap-4 sm:space-y-0",
        )}
      >
        <div
          className={cn(
            "flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4",
            showQr && "sm:flex-col",
          )}
        >
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary">
            <CheckCircle2 className="size-5 text-primary-foreground" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-primary">{t("deposit_payment_created")}</p>
            <p className="mt-0.5 font-mono text-xs break-all text-muted-foreground">
              {t("deposit_result_id")} · {result.id}
            </p>
            <p className="mt-1.5 text-sm text-foreground">
              {t("deposit_payer_sends")}{" "}
              <strong className="font-mono">{formatCop(result.gross_amount, activeLanguage)} COP</strong>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("deposit_result_fee_label")}:{" "}
              <span className="font-mono font-medium text-foreground">
                {totalFee === 0
                  ? t("deposit_result_fee_free")
                  : `${formatCop(resultFee.commission, activeLanguage)} COP`}
              </span>
            </p>
            {resultFee.taxes.map((tax) => (
              <p key={tax.code} className="mt-0.5 text-xs text-muted-foreground">
                {taxLabel(tax)}:{" "}
                <span className="font-mono font-medium text-foreground">
                  {formatCop(tax.amount, activeLanguage)} COP
                </span>
              </p>
            ))}
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("deposit_we_credit")}:{" "}
              <span className="font-mono font-medium text-foreground">
                {formatCop(result.net_amount, activeLanguage)} COP
              </span>
            </p>
          </div>
        </div>

        {/* No key on a QR with an amount: a typed transfer becomes another collection and this one expires. */}
        {showQr && (
          <PayinQrCard
            className="sm:w-96"
            src={qrSrc}
            rail="BREB"
            footer={expiresLabel ? `${t("deposit_expires_label")} ${expiresLabel}` : undefined}
            amount={`${formatCop(result.gross_amount, activeLanguage)} COP`}
          />
        )}
      </div>

      {/* Redirect rails reach this render only CONFIRMED (the payer authorized with the modal open). */}
      {isRedirect && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/25 bg-primary/5 px-3 py-2.5">
          <CheckCircle2 className="size-5 shrink-0 text-primary" aria-hidden="true" />
          <p className="text-sm font-medium text-primary">{t("deposit_pse_confirmed")}</p>
        </div>
      )}

      {/* Footer — the result branches own their actions; the dialog footer is empty on this step. */}
      <div className="flex justify-between border-t border-border pt-4">
        <ColocaButton variant="ghost" onClick={onReset}>
          {t("deposit_new")}
        </ColocaButton>
        <ColocaButton variant="primary" onClick={onClose}>
          {t("deposit_close")}
        </ColocaButton>
      </div>
    </div>
  );
}

// ─── One key's QR ─────────────────────────────────────────────────────────────

interface KeyQrViewProps {
  keyId: string | null;
  onOpenDetails: (keyId: string) => void;
}

function KeyQrView({ keyId, onOpenDetails }: KeyQrViewProps) {
  const { t: tPayins } = useTranslations("payins");
  const { state } = useBrebReceivingKey();
  const receivingKey = state?.keys.find((key) => key.id === keyId) ?? state?.key ?? null;

  if (!receivingKey) return <ColocaSkeleton className="mx-auto h-96 w-full max-w-sm rounded-2xl" />;

  return (
    <div className="mx-auto w-full max-w-sm space-y-4">
      <p className="text-sm text-muted-foreground">{tPayins("breb_key_static_qr_body")}</p>
      <BrebKeyStaticQr receivingKey={receivingKey} />
      <ColocaButton
        variant="ghost"
        icon={KeyRound}
        onClick={() => onOpenDetails(receivingKey.id)}
        className="w-full justify-center"
      >
        {tPayins("breb_key_qr_view_details")}
      </ColocaButton>
    </div>
  );
}

// ─── Root panel ───────────────────────────────────────────────────────────────

/** The views walked through, newest last, so "back" retraces the path instead of guessing it. */
interface PanelNav {
  stack: ColombiaDepositView[];
  keyId: string | null;
}

export function ColombiaDepositModal({
  wallet,
  open,
  onClose,
  initialView = "deposit",
  initialKeyId = null,
}: ColombiaDepositModalProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const { t: tPayins } = useTranslations("payins");
  const { user } = useAuth();
  const [nav, setNav] = useState<PanelNav>({ stack: [initialView], keyId: initialKeyId });
  const view = nav.stack[nav.stack.length - 1];
  const [keyStep, setKeyStep] = useState<number | null>(null);
  const [step, setStep] = useState<ModalStep>(STEP.AMOUNT);
  const [wizard, setWizard] = useState<WizardState>(INITIAL_WIZARD);
  const [priced, setPriced] = useState<PayinAmountWithFeeState>(EMPTY_PRICED);
  const [payinResult, setPayinResult] = useState<InitiatePayinResponse | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitErrorCode, setSubmitErrorCode] = useState<string | null>(null);
  // State (not a ref): it is read during render to mount StepProcessing.
  const [pendingPayload, setPendingPayload] = useState<InitiatePayinPayload | null>(null);
  // Minted per attempt so the reference is displayable BEFORE the payin exists; the payload reuses it.
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => newIdempotencyKey());

  const countryName = CURRENCY_TO_COUNTRY[wallet.currency] ?? wallet.country;
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const pseReference = idempotencyKey.replace(/-/g, "").slice(0, 10).toUpperCase();
  const isPse = wizard.method === PayinRail.PSE;
  // Every method but the Bre-B QR registers the payer with the network up-front.
  const needsPayer = colombiaMethodNeedsPayer(wizard.method);

  // The amount is typed before the payer step, so that step can only check the envelope of both payer
  // kinds; the exact cap is re-checked on confirm. Only PSE declares the kind — the other rails infer it from
  // the document, which is why the hardcoded `personType` of the Nequi/Bancolombia payer is ignored here.
  const corridors = useAvailableCorridors(organizationId ?? undefined);
  const limits = corridors.limitsFor(wizard.method, "COL", "COP", "PAYIN");
  const payerType: LimitPayerType | null = wizard.payer
    ? payerTypeForColombianPayer(isPse ? wizard.payer : { documentType: wizard.payer.documentType })
    : null;
  const confirmBounds = boundsForPayer(limits, payerType);
  const confirmViolation = priced.preview
    ? resolveAmountLimitViolation(priced.preview.gross_amount, confirmBounds)
    : null;

  // Here — and ONLY here — the signed-in user is the payer: this modal tops up their own wallet. The same
  // form on `/app/payins` is filled in for a third party who is being charged, so it starts empty.
  const payerDefaults = {
    ...(wizard.payer ?? {}),
    payerName: wizard.payer?.payerName ?? user?.full_name ?? "",
    email: wizard.payer?.email ?? user?.email ?? "",
  };

  // Opened from elsewhere straight onto the key (the wallet card), the panel lands there again next time.
  useEffect(() => {
    // eslint-disable-next-line react-hooks-eslint/set-state-in-effect
    if (open) setNav({ stack: [initialView], keyId: initialKeyId });
  }, [open, initialView, initialKeyId]);

  const openView = (next: ColombiaDepositView, keyId: string | null) =>
    setNav((current) => ({ stack: [...current.stack, next], keyId }));
  const goBack = () =>
    setNav((current) => ({
      ...current,
      stack: current.stack.length > 1 ? current.stack.slice(0, -1) : current.stack,
    }));
  const canGoBack = nav.stack.length > 1;

  const resetState = () => {
    setStep(STEP.AMOUNT);
    setWizard(INITIAL_WIZARD);
    setPriced(EMPTY_PRICED);
    setPayinResult(null);
    setSubmitError(null);
    setSubmitErrorCode(null);
    setPendingPayload(null);
    setIdempotencyKey(newIdempotencyKey());
  };

  const resetAndClose = () => {
    resetState();
    onClose();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && step === STEP.PROCESSING) return; // lock during API call
    if (!next) resetAndClose();
  };

  const handleBrebKeyChange = useCallback(
    (brebKeyId: string | null) => setWizard((current) => ({ ...current, brebKeyId })),
    [],
  );

  const handlePsePayerSubmit = (values: PsePayerValues, meta: { bankName: string }) => {
    setWizard((current) => ({ ...current, payer: values, bankName: meta.bankName }));
    setStep(STEP.CONFIRM);
  };

  // Nequi / Botón Bancolombia collect the same identity minus PSE's bank and person type; stored in the
  // PSE shape so one wizard field serves the three payer rails.
  const handleColombiaPayerSubmit = (values: ColombiaPayerValues) => {
    setWizard((current) => ({
      ...current,
      payer: { ...values, personType: "natural", bankCode: "" } as PsePayerValues,
      bankName: "",
    }));
    setStep(STEP.CONFIRM);
  };

  const handleConfirm = () => {
    if (confirmViolation) return;
    // The runtime scopes the payin by organization — without one the DTO is unbuildable.
    if (!organizationId) {
      setSubmitError(t("deposit_error_no_org"));
      setStep(STEP.RESULT);
      return;
    }
    // The preview's resolved gross — the amount whose commission the user just confirmed.
    const amountCop = priced.preview!.gross_amount;
    const payer = wizard.payer;
    const payerInput = payer
      ? {
          organizationId,
          amountCop,
          payerName: payer.payerName,
          documentType: payer.documentType,
          documentNumber: payer.documentNumber,
          email: payer.email,
          phone: payer.phone,
          address: payer.address,
          idempotencyKey,
        }
      : null;
    setPendingPayload(
      isPse && payer && payerInput
        ? buildColombiaPsePayinPayload({
            ...payerInput,
            bankCode: payer.bankCode,
            userType: psePayerUserType(payer.personType),
          })
        : wizard.method === PayinRail.NEQUI && payerInput
          ? buildColombiaNequiPayinPayload(payerInput)
          : wizard.method === PayinRail.BANCOLOMBIA_BUTTON && payerInput
            ? buildColombiaBancolombiaButtonPayinPayload(payerInput)
            : buildColombiaBrebPayinPayload({
                organizationId,
                amountCop,
                idempotencyKey,
                brebReceivingKeyId: wizard.brebKeyId,
              }),
    );
    setStep(STEP.PROCESSING);
  };

  // Bre-B has no payer to collect, so it skips the middle step entirely — and its stepper says so.
  const stepperSteps: ColocaStepperStep[] = needsPayer
    ? [
        { label: tPayins("step_amount") },
        { label: tPayins("step_payer") },
        { label: tPayins("step_confirm") },
      ]
    : [{ label: tPayins("step_amount") }, { label: tPayins("step_confirm") }];
  const stepperCurrent = needsPayer ? step : step === STEP.AMOUNT ? 0 : 1;
  // Not on the first screen: the keys above the amount are not a step of the charge.
  const showStepper = step > STEP.AMOUNT && step <= STEP.CONFIRM;

  const isKeyView = view === "key";
  const isQrView = view === "qr";
  const isDepositView = view === "deposit";

  const footer = (() => {
    if (isKeyView || isQrView) {
      // Opened straight onto a key there is nothing to go back to; the key wizard owns its own "Back".
      return canGoBack ? (
        <ColocaButton variant="ghost" icon={ChevronLeft} onClick={goBack}>
          {t("deposit_back")}
        </ColocaButton>
      ) : (
        <ColocaButton variant="ghost" onClick={resetAndClose}>
          {tPayins("breb_key_close")}
        </ColocaButton>
      );
    }
    if (step === STEP.AMOUNT) {
      return (
        <>
          <ColocaButton variant="ghost" onClick={resetAndClose}>
            {t("deposit_close")}
          </ColocaButton>
          {/* Keyed apart from the payer step's submit button, which takes this same slot: React would reuse
              the element, it turns into `type="submit"` during this very click, and the click's default action
              then submits the still-empty payer form, which opens with its errors already showing. */}
          <ColocaButton
            key="amount-continue"
            variant="primary"
            iconTrailing={ArrowRight}
            disabled={!priced.preview}
            onClick={() => setStep(needsPayer ? STEP.PAYER : STEP.CONFIRM)}
          >
            {t("deposit_continue")}
          </ColocaButton>
        </>
      );
    }
    if (step === STEP.PAYER) {
      return (
        <>
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep(STEP.AMOUNT)}>
            {t("deposit_edit_amount")}
          </ColocaButton>
          <ColocaButton
            key="payer-submit"
            type="submit"
            form={PAYER_FORM_ID}
            variant="primary"
            iconTrailing={ArrowRight}
          >
            {t("deposit_continue")}
          </ColocaButton>
        </>
      );
    }
    if (step === STEP.CONFIRM) {
      return (
        <>
          <ColocaButton
            variant="ghost"
            icon={ChevronLeft}
            onClick={() => setStep(needsPayer ? STEP.PAYER : STEP.AMOUNT)}
          >
            {t("deposit_edit")}
          </ColocaButton>
          <ColocaButton variant="primary" onClick={handleConfirm} disabled={!!confirmViolation}>
            {t("deposit_create_payment")}
          </ColocaButton>
        </>
      );
    }
    // Processing is locked, and each result branch owns its own actions.
    return undefined;
  })();

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={handleOpenChange}
      size="lg"
      showCloseButton={step !== STEP.PROCESSING}
      title={
        isKeyView
          ? tPayins("breb_key_label")
          : isQrView
            ? tPayins("breb_key_qr_view_title")
            : t("card_action_load")
      }
      description={`${countryName} · ${wallet.currency}`}
      footer={footer}
      // Wizards that draw their own action row (the result step) pin it; a real footer never needs the pin.
      pinLastChild={!footer}
      // Fixed under the header: on the payer step the body scrolls, and losing sight of which step you are on
      // is exactly what a stepper exists to prevent.
      toolbar={
        isKeyView
          ? keyStep !== null && <BrebKeyWizardStepper currentStep={keyStep} />
          : isDepositView &&
            showStepper && (
              <ColocaStepper
                steps={stepperSteps}
                currentStep={stepperCurrent}
                aria-label={t("deposit_modal_title")}
              />
            )
      }
    >
      {isKeyView && <BrebKeyFlow onStepChange={setKeyStep} initialKeyId={nav.keyId} />}
      {isQrView && <KeyQrView keyId={nav.keyId} onOpenDetails={(keyId) => openView("key", keyId)} />}

      {isDepositView && step === STEP.AMOUNT && (
        <StepAmount
          method={wizard.method}
          organizationId={organizationId}
          priced={priced}
          payerType={payerType}
          onPricedChange={setPriced}
          onMethodChange={(method) => setWizard((current) => ({ ...current, method }))}
          brebKeyId={wizard.brebKeyId}
          onBrebKeyChange={handleBrebKeyChange}
          onOpenKey={() => openView("key", null)}
          onShowKeyQr={(keyId) => openView("qr", keyId)}
        />
      )}

      {isDepositView &&
        step === STEP.PAYER &&
        (isPse ? (
          <PsePayerStep
            formId={PAYER_FORM_ID}
            defaults={payerDefaults}
            reference={pseReference}
            amount={priced.preview ? formatCop(priced.preview.gross_amount, activeLanguage) : null}
            currency="COP"
            onSubmit={handlePsePayerSubmit}
          />
        ) : (
          <ColombiaPayerStep
            formId={PAYER_FORM_ID}
            defaults={payerDefaults}
            reference={pseReference}
            amount={priced.preview ? formatCop(priced.preview.gross_amount, activeLanguage) : null}
            currency="COP"
            phoneLabel={wizard.method === PayinRail.NEQUI ? t("deposit_form_nequi") : undefined}
            onSubmit={handleColombiaPayerSubmit}
          />
        ))}

      {isDepositView && step === STEP.CONFIRM && priced.preview && (
        <StepConfirm
          wizard={wizard}
          preview={priced.preview}
          limitViolation={confirmViolation}
          limitBounds={confirmBounds}
          payerType={payerType}
          onEditAmount={() => setStep(STEP.AMOUNT)}
        />
      )}

      {isDepositView && step === STEP.PROCESSING && pendingPayload && (
        <StepProcessing
          payload={pendingPayload}
          onSuccess={(data) => {
            setPayinResult(data);
            setStep(STEP.RESULT);
          }}
          onError={(msg, code) => {
            setSubmitError(msg);
            setSubmitErrorCode(code);
            setStep(STEP.RESULT);
          }}
        />
      )}

      {isDepositView && step === STEP.RESULT && (
        <StepResult
          result={payinResult}
          error={submitError}
          errorCode={submitErrorCode}
          wizard={wizard}
          pseReference={pseReference}
          onReset={resetState}
          onClose={resetAndClose}
        />
      )}
    </ColocaDrawer>
  );
}
